import React from "react";
import {Grid} from "@mui/material";
import {observer} from "mobx-react-lite";
import {useStores} from "../../../stores";
import './KoiKeyDataFacts.scss';
import {toCurrency} from "../../../components/currency-component/CurrencyClasses";
import type {
    FilterSpecification,
    Options
} from "../../../components/visualization/collapsible-indent-tree/CollapsibleIndentTree";
import {CollapsibleIndentTree} from "../../../components/visualization/collapsible-indent-tree/CollapsibleIndentTree";
import {FilteredSankeyComponent} from "./FilteredSankeyComponent";
import {Subject} from "rxjs";
import type {MatKpiTreeData} from "../../../services/ApiTypes";
import {MainPageFacade} from "../../../components/main/page/MainPageFacade.tsx";
import {KoiKeyDataFactsToolbar} from "./KoiKeyDataFactsToolbar.tsx";
import {CARD_CONFIG} from "../../../components/dashboard/DashboardCardConfigs.tsx";

export const KoiKeyDataFacts: React.FC = observer(() => {
    const {bagStore, p} = useStores();

    const [filter, setFilter] = React.useState<number>(0);
    const hiddenRoot = false;
    const [hiddenUncat, setHiddenUncat] = React.useState<boolean>(true);

    const $filter = React.useMemo<FilterSpecification>(() => ({
        byLevel: new Subject<number>(),
    }), []);

    const options = React.useMemo<Partial<Options<MatKpiTreeData>> | undefined>(() => $filter ? {
        $filter,
        columns: [
            {
                columnTitle: 'Total spend',
                getValue: (d, c) => {
                    return String(c.hiddenRoot && d.depth === 0 ? '' : toCurrency(p.currencySymbol, d.data.values.spend));
                },
                xOffset: 540,
            },
            {
                columnTitle: 'Suppliers',
                getValue: (d, c) =>
                    String(c.hiddenRoot && d.depth === 0 ? '' : (d.data.values.distinct_suppliers || '-')),
                xOffset: 610,
            },
            {
                columnTitle: 'Transactions',
                getValue: (d, c) =>
                    String(c.hiddenRoot && d.depth === 0 ? '' : (d.data.values.parts || '-')),
                xOffset: 700,
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
    } : undefined, [$filter])

    return (
        <MainPageFacade
            title={CARD_CONFIG['koi-key-data-facts'].title}
            className="koi-kdf-page"
            actionBarEnd={<KoiKeyDataFactsToolbar
                $filter={$filter}
                filter={filter}
                setFilter={setFilter}
                hiddenUncat={hiddenUncat}
                setHiddenUncat={setHiddenUncat}
            />}>
            {p.p.enableSankey &&
                <div className="page-item sankey-item">
                    <FilteredSankeyComponent/>
                </div>
            }

            {bagStore.kpi.taxonomyData && <>
                <section className="page-item taxonomy-tree-item">
                    <Grid container justifyContent="center">
                        <Grid size={{xs: 12, md: 10, lg: 8, xl: 6}}>
                            <CollapsibleIndentTree
                                data={bagStore.kpi.taxonomyData}
                                options={options}
                                filter={filter}
                                hiddenRoot={hiddenRoot}
                                hiddenUncat={hiddenUncat}
                            />
                        </Grid>
                    </Grid>
                </section>
            </>}
        </MainPageFacade>
    );
});
